/**
 * Tiny typed fetch wrapper (Phase U0).
 *
 * Every request/response shape is derived from the generated `paths`/`components`
 * types in ./schema (produced by `pnpm gen:api` from api/openapi.yaml). There are
 * NO hand-written response interfaces here — that is a CLAUDE.md CI failure.
 *
 * Guardrails encoded here:
 * - Base URL is fixed to the platform API `/api/v1`; the frontend never calls
 *   Dynizer, the Pipeline API, the MAG-API or any LLM provider directly.
 * - The bearer JWT lives in a module-scoped variable — in memory ONLY. It is
 *   never written to localStorage/sessionStorage/cookies.
 *
 * No component or surface calls this in U0 (generation-only). The auth flow and
 * refresh-on-401 that use it arrive in Phase U1.
 */
import type { paths, components } from './schema';

export const API_BASE_URL = '/api/v1';

/** The single error envelope for the whole API (F5): `{ error: {...} }`. */
export type ApiErrorEnvelope = components['schemas']['Error'];

/** Thrown for any non-2xx response; carries the parsed error envelope if present. */
export class ApiError extends Error {
  readonly status: number;
  readonly envelope: ApiErrorEnvelope | undefined;
  readonly requestId: string | undefined;

  constructor(status: number, envelope: ApiErrorEnvelope | undefined) {
    super(envelope?.error?.message ?? `API request failed with status ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.envelope = envelope;
    this.requestId = envelope?.error?.request_id;
  }
}

// ── in-memory auth (never persisted) ───────────────────────────────────────
let bearerToken: string | null = null;
export function setAuthToken(token: string | null): void {
  bearerToken = token;
}
export function getAuthToken(): string | null {
  return bearerToken;
}

/**
 * Registered by the auth layer. Invoked once when the API returns 401 on an
 * authenticated call, so the app can drop the in-memory session and redirect to
 * login. The contract has no refresh-token endpoint (the JWT is a Dynizer
 * pass-through), so "refresh on 401" means re-authenticate: clear + redirect.
 */
let unauthorizedHandler: (() => void) | null = null;
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

// ── type plumbing: pull request/response shapes out of `paths` ──────────────
type HttpMethod = 'get' | 'put' | 'post' | 'delete' | 'options' | 'head' | 'patch';

/** Operations under a path that actually define the given method. */
type MethodOf<P extends keyof paths> = keyof paths[P] & HttpMethod;

type OperationOf<P extends keyof paths, M extends MethodOf<P>> = paths[P][M];

type JsonContent<T> = T extends { content: { 'application/json': infer B } } ? B : never;

/** The JSON body of the first declared 2xx response (or `void` when there is none). */
type SuccessBody<Op> = Op extends { responses: infer R }
  ? R extends { 200: infer T }
    ? JsonContent<T>
    : R extends { 201: infer T }
      ? JsonContent<T>
      : R extends { 202: infer T }
        ? JsonContent<T>
        : void
  : void;

type RequestBody<Op> = Op extends { requestBody: { content: { 'application/json': infer B } } }
  ? B
  : Op extends { requestBody?: { content: { 'application/json': infer B } } }
    ? B | undefined
    : undefined;

type PathParams<Op> = Op extends { parameters: { path: infer P } }
  ? P extends null | undefined
    ? Record<string, never>
    : P
  : Record<string, never>;

type QueryParams<Op> = Op extends { parameters: { query?: infer Q } }
  ? Q extends null | undefined
    ? Record<string, never>
    : Q
  : Record<string, never>;

export interface RequestOptions<Op> {
  path?: PathParams<Op>;
  query?: QueryParams<Op>;
  body?: RequestBody<Op>;
  /** Idempotency-Key header for mutating ingest/job calls (F5). */
  idempotencyKey?: string;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Set on the login call so a 401 there does not trigger a redirect loop. */
  skipAuthRedirect?: boolean;
}

function interpolate(template: string, params: Record<string, unknown> | undefined): string {
  if (!params) return template;
  return template.replace(/\{([^}]+)\}/g, (_match, key: string) => {
    const value = params[key];
    if (value === undefined || value === null) {
      throw new Error(`Missing path parameter "${key}" for ${template}`);
    }
    return encodeURIComponent(String(value));
  });
}

function buildQuery(query: Record<string, unknown> | undefined): string {
  if (!query) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      for (const item of value) search.append(key, String(item));
    } else {
      search.append(key, String(value));
    }
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Perform a typed request against the platform API. The response type is inferred
 * from the OpenAPI contract for `(pathTemplate, method)` — callers never annotate
 * a response shape by hand.
 */
export async function apiRequest<
  P extends keyof paths & string,
  M extends MethodOf<P>,
>(
  pathTemplate: P,
  method: M,
  options: RequestOptions<OperationOf<P, M>> = {},
): Promise<SuccessBody<OperationOf<P, M>>> {
  const url =
    API_BASE_URL +
    interpolate(pathTemplate, options.path as Record<string, unknown> | undefined) +
    buildQuery(options.query as Record<string, unknown> | undefined);

  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers };
  if (bearerToken) headers.Authorization = `Bearer ${bearerToken}`;
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;

  const hasBody = options.body !== undefined;
  if (hasBody) headers['Content-Type'] = 'application/json';

  const response = await fetch(url, {
    method: method.toUpperCase(),
    headers,
    body: hasBody ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  });

  if (!response.ok) {
    let envelope: ApiErrorEnvelope | undefined;
    try {
      envelope = (await response.json()) as ApiErrorEnvelope;
    } catch {
      envelope = undefined;
    }
    if (response.status === 401 && !options.skipAuthRedirect) {
      unauthorizedHandler?.();
    }
    throw new ApiError(response.status, envelope);
  }

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as SuccessBody<OperationOf<P, M>>;
  }
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    return undefined as SuccessBody<OperationOf<P, M>>;
  }
  return (await response.json()) as SuccessBody<OperationOf<P, M>>;
}
